import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Info } from "lucide-react";

interface AddHoursModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentCode: string;
  studentName: string;
  orderId: string;
  manualAddHoursAt: string | null;
  manualAddHourBy: string | null;
  onSuccess: () => void;
}

export const AddHoursModal = ({
  open,
  onOpenChange,
  studentCode,
  studentName,
  orderId,
  manualAddHoursAt,
  manualAddHourBy,
  onSuccess,
}: AddHoursModalProps) => {
  const [hoursValue, setHoursValue] = useState<string>("");
  const [remark, setRemark] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const isLocked = Boolean(manualAddHoursAt);

  useEffect(() => {
    if (open && isLocked) {
      toast({
        title: "ไม่สามารถเพิ่มชั่วโมงได้",
        description: "คำสั่งซื้อนี้ถูกเพิ่มชั่วโมงไปแล้ว",
        variant: "destructive",
      });
    }
  }, [open, isLocked, toast]);

  const handleSubmit = async () => {
    if (isLocked) {
      toast({
        title: "ไม่สามารถเพิ่มชั่วโมงได้",
        description: "คำสั่งซื้อนี้ถูกเพิ่มชั่วโมงไปแล้ว",
        variant: "destructive",
      });
      return;
    }

    // Validate inputs
    if (!hoursValue || hoursValue.trim() === "") {
      toast({
        title: "กรุณากรอกข้อมูล",
        description: "กรุณากรอกจำนวนชั่วโมง",
        variant: "destructive",
      });
      return;
    }

    const hours = parseFloat(hoursValue);
    if (isNaN(hours)) {
      toast({
        title: "ข้อมูลไม่ถูกต้อง",
        description: "จำนวนชั่วโมงต้องเป็นตัวเลข",
        variant: "destructive",
      });
      return;
    }

    if (!remark || remark.trim() === "") {
      toast({
        title: "กรุณากรอกข้อมูล",
        description: "กรุณากรอกหมายเหตุ",
        variant: "destructive",
      });
      return;
    }

    if (remark.length > 500) {
      toast({
        title: "ข้อมูลไม่ถูกต้อง",
        description: "หมายเหตุต้องไม่เกิน 500 ตัวอักษร",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user?.email) {
        throw new Error("ไม่สามารถตรวจสอบข้อมูลผู้ใช้ได้");
      }

      const { data, error } = await supabase.functions.invoke(
        "manage-student-hours",
        {
          body: {
            studentCode,
            hoursValue: hours,
            remark: remark.trim(),
            email: user.email,
            orderId,
          },
        }
      );

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.message || "Failed to update hours");
      }

      toast({
        title: "อัพเดตชั่วโมงสำเร็จ",
        description: `${hours > 0 ? "เพิ่ม" : "ลด"}ชั่วโมงสำหรับนักเรียน ${studentCode} แล้ว`,
      });

      // Reset form
      setHoursValue("");
      setRemark("");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error updating hours:", error);
      toast({
        title: "เกิดข้อผิดพลาด",
        description:
          error instanceof Error ? error.message : "ไม่สามารถอัพเดตชั่วโมงได้",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setHoursValue("");
    setRemark("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>เพิ่ม/ลดชั่วโมงเรียน</DialogTitle>
          <DialogDescription>
            กรอกจำนวนชั่วโมงที่ต้องการเพิ่มหรือลด
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isLocked && (
            <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <Info className="h-5 w-5 flex-shrink-0" />
              <div>
                <div className="font-medium">คำสั่งซื้อนี้ถูกเพิ่มชั่วโมงแล้ว</div>
                <div>
                  โดย: {manualAddHourBy || "ไม่ทราบ"} | เมื่อ:{" "}
                  {manualAddHoursAt
                    ? new Date(manualAddHoursAt).toLocaleString("th-TH", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : "-"}
                </div>
              </div>
            </div>
          )}

          {/* Student Info Card */}
          <div className="rounded-lg bg-muted p-3 space-y-1">
            <div className="text-sm font-medium">ข้อมูลนักเรียน</div>
            <div className="text-sm text-muted-foreground">
              รหัส: <span className="font-medium text-foreground">{studentCode}</span>
              {" | "}
              ชื่อ: <span className="font-medium text-foreground">{studentName}</span>
            </div>
          </div>

          {/* Info Alert */}
          <div className="flex gap-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 p-3 border border-blue-200 dark:border-blue-800">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900 dark:text-blue-100">
              <div className="font-medium mb-1">
                ใส่ตัวเลขบวกเพื่อเพิ่มชั่วโมง หรือตัวเลขลบเพื่อลดชั่วโมง
              </div>
              <div className="text-blue-700 dark:text-blue-300">
                ตัวอย่าง: 2, -1.5, 0.5
              </div>
            </div>
          </div>

          {/* Hours Input */}
          <div className="space-y-2">
            <Label htmlFor="hours">
              จำนวนชั่วโมง <span className="text-destructive">*</span>
            </Label>
            <Input
              id="hours"
              type="number"
              step="0.5"
              placeholder="2"
              value={hoursValue}
              onChange={(e) => setHoursValue(e.target.value)}
              disabled={isSubmitting || isLocked}
              className="w-1/2"
            />
          </div>

          {/* Remark Textarea */}
          <div className="space-y-2">
            <Label htmlFor="remark">
              หมายเหตุ <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="remark"
              placeholder="ระบุเหตุผลในการเพิ่ม/ลดชั่วโมง"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              disabled={isSubmitting || isLocked}
              rows={4}
              maxLength={500}
            />
            <div className="text-xs text-muted-foreground text-right">
              {remark.length}/500 ตัวอักษร
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || isLocked}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLocked ? "เพิ่มแล้ว" : "ยืนยัน"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
